import { useState, useEffect } from "react";
import { evolveBadges } from "@/lib/badges";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api, { BACKEND_URL } from "@/lib/api";
import { getPlaylistCoverUrl, getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";

// Microphone Icon Component
const MicIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
  </svg>
);

// Badge Component
const Badge = ({ type }) => {
  const badges = {
    founder: { label: "Founder", icon: "👑", className: "bg-gradient-to-r from-yellow-500 to-amber-600 text-black" },
    admin: { label: "Admin", icon: "⚡", className: "bg-purple-600 text-white" },
    spotlight_winner: { label: "Spotlight", icon: "🏆", className: "bg-gradient-to-r from-pink-500 to-purple-600 text-white" },
    producer: { label: "Producer", icon: "🎧", className: "bg-blue-600 text-white" }
  };
  const badge = badges[type];
  if (!badge) return null;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold inline-flex items-center gap-1 ${badge.className}`}>
      <span>{badge.icon}</span>
      {badge.label}
    </span>
  );
};

const CommunityPage = () => {
  const [user, setUser] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [publicPlaylists, setPublicPlaylists] = useState([]);
  const [stats, setStats] = useState({ artists: 0, tracks: 0, plays: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      // User not logged in - that's fine for viewing community
    }
  };

  const fetchData = async () => {
    try {
      const [activityRes, statsRes, playlistsRes] = await Promise.all([
        api.get('/activity/feed').catch(() => ({ data: [] })),
        api.get('/stats/community').catch(() => ({ data: { artists: 0, tracks: 0, plays: 0 } })),
        api.get('/playlists/public?limit=8').catch(() => ({ data: { playlists: [] } }))
      ]);
      setActivityFeed(activityRes.data || []);
      setStats(statsRes.data || { artists: 0, tracks: 0, plays: 0 });
      setPublicPlaylists(playlistsRes.data?.playlists || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProfilePhoto = (item) => getProfilePhotoUrl(item);

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const getActivityIcon = (type) => {
    const icons = {
      new_artist: '👋',
      new_track: '🎵',
      featured: '⭐',
      milestone: '🎉'
    };
    return icons[type] || '📢';
  };

  return (
    <div className="min-h-screen bg-[#08080c]">
      {/* Navigation */}
      <SharedNavigation currentUser={user} activePage="community" />

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 border-b border-purple-900/30" data-testid="community-hero">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4">
            RGM <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">Community</span>
          </h1>
          <p className="text-xl text-gray-400 mb-8">
            Where independent artists connect, collaborate, and rise together.
          </p>
          
          {/* Community Stats */}
          <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
            <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-4" data-testid="stat-artists">
              <p className="text-3xl font-bold text-white">{stats.artists}</p>
              <p className="text-gray-400 text-sm">Artists</p>
            </div>
            <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-4" data-testid="stat-tracks">
              <p className="text-3xl font-bold text-white">{stats.tracks}</p>
              <p className="text-gray-400 text-sm">Tracks</p>
            </div>
            <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-4" data-testid="stat-plays">
              <p className="text-3xl font-bold text-white">{stats.plays.toLocaleString()}</p>
              <p className="text-gray-400 text-sm">Plays</p>
            </div>
          </div>
        </div>
      </section>

      {/* Activity Feed Section */}
      <section className="py-12 px-4" data-testid="activity-section">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Activity Feed</h2>
            <span className="text-gray-400 text-sm">Live updates</span>
          </div>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
              <p className="mt-4 text-gray-400">Loading activity...</p>
            </div>
          ) : activityFeed.length > 0 ? (
            <div className="space-y-4" data-testid="activity-feed">
              {activityFeed.map((item, index) => (
                <Card
                  key={item.id || index}
                  className="p-4 hover:bg-purple-900/20 transition-colors"
                  style={{ background: 'rgba(42, 26, 58, 0.3)', borderColor: 'rgba(138, 43, 226, 0.2)' }}
                  data-testid={`activity-item-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="text-2xl">{getActivityIcon(item.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {item.user_id && (
                          <Link to={`/artist/${item.user_id}`} className="flex items-center gap-2">
                            <img
                              src={optimizeImage(getProfilePhoto(item), 'avatar')}
                              alt={item.artist_name || item.name}
                              className="w-8 h-8 rounded-full object-cover"
                              onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop'; }}
                            />
                            <span className="font-semibold text-white hover:text-purple-400 transition-colors">
                              {item.artist_name || item.name}
                            </span>
                          </Link>
                        )}
                        {item.badges && evolveBadges(item.badges).map(badge => (
                          <Badge key={badge} type={badge} />
                        ))}
                      </div>
                      <p className="text-gray-300">
                        {item.message}
                        {item.type === 'new_artist' && item.referrer_user_id && item.referrer_name && (
                          <>
                            {' '}
                            <Link
                              to={`/artist/${item.referrer_user_id}`}
                              className="text-purple-300 hover:text-purple-200 font-medium underline-offset-2 hover:underline"
                              data-testid={`activity-referrer-${item.user_id}`}
                            >
                              {item.referrer_name}
                            </Link>
                          </>
                        )}
                      </p>
                      {item.track_title && (
                        <Link to={`/track/${item.submission_id}`} className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm">
                          🎵 {item.track_title}
                          {item.type === 'featured' && (
                            <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-yellow-400 to-amber-500 text-black">
                              🎖️ Featured
                            </span>
                          )}
                        </Link>
                      )}
                      <p className="text-gray-500 text-xs mt-1">{formatTimeAgo(item.created_at)}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card
              className="p-12 text-center"
              style={{ background: 'rgba(42, 26, 58, 0.3)', borderColor: 'rgba(138, 43, 226, 0.2)' }}
            >
              <p className="text-gray-400 mb-4">No recent activity yet.</p>
              <Link to="/explore">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white">
                  Explore Artists
                </Button>
              </Link>
            </Card>
          )}
        </div>
      </section>

      {/* Join CTA */}
      {!user && (
        <section className="py-12 px-4 border-t border-purple-900/30" data-testid="join-cta">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Join the Community</h2>
            <p className="text-gray-400 mb-6">Create your artist profile and start sharing your music today.</p>
            <Link to="/login">
              <Button size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold px-8">
                Get Started
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* Community Playlists Section */}
      {publicPlaylists.length > 0 && (
        <section className="py-12 px-4 border-t border-purple-900/30" data-testid="community-playlists">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <span className="text-xl">🎵</span>
                  Community Playlists
                </h2>
                <p className="text-gray-400 mt-1">Discover curated collections from our community</p>
              </div>
              <Link to="/playlists">
                <Button variant="outline" size="sm" className="text-purple-400 border-purple-500/50">
                  Browse All
                </Button>
              </Link>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {publicPlaylists.map((playlist) => (
                <Link
                  key={playlist.playlist_id}
                  to={`/playlist/${playlist.playlist_id}`}
                  className="group"
                  data-testid={`community-playlist-${playlist.playlist_id}`}
                >
                  <Card
                    className="overflow-hidden hover:border-purple-500/50 transition-all"
                    style={{ background: 'rgba(42, 26, 58, 0.3)', borderColor: 'rgba(138, 43, 226, 0.2)' }}
                  >
                    <div className="aspect-square relative overflow-hidden">
                      <img
                        src={getPlaylistCoverUrl(playlist)}
                        alt={playlist.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1614149162883-504ce4d13909?w=300&h=300&fit=crop'; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                      
                      {/* Play Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                          <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3">
                      <h3 className="text-white font-semibold truncate group-hover:text-purple-300 transition-colors">
                        {playlist.name}
                      </h3>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-gray-500 text-sm truncate">
                          by {playlist.creator_name || 'Unknown Artist'}
                        </p>
                        <span className="text-gray-600 text-xs">
                          {playlist.track_ids?.length || 0} tracks
                        </span>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default CommunityPage;
